export const NODE_OUTER = 84
export const NODE_CIRCLE = 72
export const NODE_HALF = 42
export const PAIR_GAP = 72
export const ROW_GAP = 10
export const CORNER_RADIUS = 40
export const ROW_STRIDE = NODE_OUTER + ROW_GAP
export const STROKE_WIDTH = 2
export const DASH = [6, 4] as const

// Horizontal gutter so the side U-turn loops don't run flush to the
// container edge (clipped on small screens). Matches the mx-[16px] used
// by the goal card / milestone elsewhere in the journey.
export const EDGE_INSET = 16

export const WEEK_ROW_COUNT = 5
export const WEEK_CONTENT_HEIGHT = WEEK_ROW_COUNT * ROW_STRIDE

export const MILESTONE_TOP_GAP = 16
export const MILESTONE_CARD_CORNER = 24
const MILESTONE_EXTRA_GAP = MILESTONE_TOP_GAP - ROW_GAP
export const MILESTONE_TOP_Y = WEEK_ROW_COUNT * ROW_STRIDE + MILESTONE_EXTRA_GAP

// day index (0-6) -> zigzag row (0-4)
export const WEEK_DAY_TO_ROW = [0, 1, 1, 2, 3, 3, 4] as const

export interface DayPlacement {
  column: -1 | 0 | 1
  rowIndex: number
}

// column: -1 left, 0 center, 1 right
export const DAY_PATH: DayPlacement[] = [
  { column: 0, rowIndex: 0 },
  { column: -1, rowIndex: 1 },
  { column: 1, rowIndex: 1 },
  { column: 0, rowIndex: 2 },
  { column: -1, rowIndex: 3 },
  { column: 1, rowIndex: 3 },
  { column: 0, rowIndex: 4 },
]

export interface Point {
  x: number
  y: number
}

export function nodeCenter(
  placement: { column: number; rowIndex: number },
  width: number,
): Point {
  const centerX = width / 2
  const offset = PAIR_GAP / 2 + NODE_HALF
  const x =
    placement.column === -1
      ? centerX - offset
      : placement.column === 1
        ? centerX + offset
        : centerX
  const y = placement.rowIndex * ROW_STRIDE + NODE_OUTER / 2
  return { x, y }
}

type Direction = 'horizontal' | 'leftSweep' | 'rightSweep'

export function computeZigzagDirections(positions: Point[]): Direction[] {
  const out: Direction[] = []
  let nextSweepLeft = true
  for (let i = 0; i < positions.length - 1; i++) {
    if (positions[i].y === positions[i + 1].y) {
      out.push('horizontal')
    } else {
      out.push(nextSweepLeft ? 'leftSweep' : 'rightSweep')
      nextSweepLeft = !nextSweepLeft
    }
  }
  return out
}

function segmentStart(
  positions: Point[],
  directions: Direction[],
  i: number,
): Point {
  const from = positions[i]
  if (directions[i] === 'leftSweep') return { x: from.x - NODE_HALF, y: from.y }
  if (directions[i] === 'rightSweep')
    return { x: from.x + NODE_HALF, y: from.y }
  const sign = positions[i + 1].x > from.x ? 1 : -1
  return { x: from.x + sign * NODE_HALF, y: from.y }
}

const round = (n: number) => Math.round(n * 100) / 100

// Build the dashed serpentine path for one week as an SVG `d` string.
export function buildWeekPath(width: number): string {
  const positions = DAY_PATH.map((p) => nodeCenter(p, width))
  const directions = computeZigzagDirections(positions)
  const cr = CORNER_RADIUS
  const half = NODE_HALF
  const leftEdge = EDGE_INSET
  const rightEdge = width - EDGE_INSET

  const cmds: string[] = []
  const line = (x: number, y: number) => cmds.push(`L ${round(x)} ${round(y)}`)
  const arc = (
    left: number,
    top: number,
    right: number,
    bottom: number,
    startDeg: number,
    sweepDeg: number,
  ) => {
    const cx = (left + right) / 2
    const cy = (top + bottom) / 2
    const rx = (right - left) / 2
    const ry = (bottom - top) / 2
    const endRad = ((startDeg + sweepDeg) * Math.PI) / 180
    const ex = cx + rx * Math.cos(endRad)
    const ey = cy + ry * Math.sin(endRad)
    const sweepFlag = sweepDeg >= 0 ? 1 : 0
    const largeArc = Math.abs(sweepDeg) > 180 ? 1 : 0
    cmds.push(
      `A ${round(rx)} ${round(ry)} 0 ${largeArc} ${sweepFlag} ${round(ex)} ${round(ey)}`,
    )
  }

  const first = segmentStart(positions, directions, 0)
  cmds.push(`M ${round(first.x)} ${round(first.y)}`)

  for (let i = 0; i < positions.length - 1; i++) {
    const from = positions[i]
    const to = positions[i + 1]
    switch (directions[i]) {
      case 'horizontal': {
        const sign = to.x > from.x ? 1 : -1
        line(to.x - sign * half, to.y)
        break
      }
      case 'leftSweep': {
        line(leftEdge + cr, from.y)
        arc(leftEdge, from.y, leftEdge + 2 * cr, from.y + 2 * cr, 270, -90)
        line(leftEdge, to.y - cr)
        arc(leftEdge, to.y - 2 * cr, leftEdge + 2 * cr, to.y, 180, -90)
        line(to.x - half, to.y)
        break
      }
      case 'rightSweep': {
        line(rightEdge - cr, from.y)
        arc(rightEdge - 2 * cr, from.y, rightEdge, from.y + 2 * cr, 270, 90)
        line(rightEdge, to.y - cr)
        arc(rightEdge - 2 * cr, to.y - 2 * cr, rightEdge, to.y, 0, 90)
        line(to.x + half, to.y)
        break
      }
    }
    if (i < positions.length - 2) {
      const next = segmentStart(positions, directions, i + 1)
      line(next.x, next.y)
    }
  }

  const day7 = positions[positions.length - 1]
  // Anchored to the milestone card's corner (mx-[16px] card), independent of
  // the loop inset above.
  const verticalX = MILESTONE_CARD_CORNER
  line(day7.x - half, day7.y)
  line(verticalX + cr, day7.y)
  arc(verticalX, day7.y, verticalX + 2 * cr, day7.y + 2 * cr, 270, -90)
  line(verticalX, MILESTONE_TOP_Y)

  return cmds.join(' ')
}
